import { createContext, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { dict } from "./translations";

const I18nCtx = createContext({ lang: "en", t: (k) => k, lp: (p) => p, stripLang: (p) => p });

export const LANGS = ["en", "pl", "de", "es", "pt", "fr", "it", "tr", "ar", "hi", "zh", "ja", "ko", "ru"];
export const ALT_LANGS = ["pl", "de", "es", "pt", "fr", "it", "tr", "ar", "hi", "zh", "ja", "ko", "ru"]; // languages that use a URL prefix (English is default, no prefix)
export const RTL_LANGS = ["ar"];
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

function detectLang(pathname) {
  const seg = pathname.split("/")[1];
  return ALT_LANGS.includes(seg) ? seg : "en";
}

function stripLang(pathname) {
  const seg = pathname.split("/")[1];
  if (ALT_LANGS.includes(seg)) {
    const rest = pathname.slice(seg.length + 1);
    return rest === "" ? "/" : rest;
  }
  return pathname;
}

function get(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

export function LanguageProvider({ children }) {
  const loc = useLocation();
  const lang = detectLang(loc.pathname);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGS.includes(lang) ? "rtl" : "ltr";
  }, [lang]);

  const t = (key) => {
    const v = get(dict[lang], key);
    if (v !== undefined) return v;
    const fb = get(dict.en, key);
    return fb !== undefined ? fb : key;
  };

  const lp = (path) => {
    if (lang === "en") return path;
    return path === "/" ? `/${lang}` : `/${lang}${path}`;
  };

  return <I18nCtx.Provider value={{ lang, t, lp, stripLang }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);

export function injectJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export { stripLang };

function setMeta(name, content, attr = "name") {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const OG_LOCALE = { pl: "pl_PL", en: "en_US", de: "de_DE", es: "es_ES", pt: "pt_PT", fr: "fr_FR", it: "it_IT", tr: "tr_TR", ar: "ar_AR", hi: "hi_IN", zh: "zh_CN", ja: "ja_JP", ko: "ko_KR", ru: "ru_RU" };

// Dynamic-page SEO for routes not in the static seo map (e.g. /rankings/{slug}).
// Sets title, description, OG, canonical + hreflang alternates and JSON-LD.
export function usePageSeo({ title, desc, path, image, jsonLd, jsonLdId = "page-jsonld" }) {
  const { lang } = useI18n();
  const loc = useLocation();
  const jsonKey = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    if (title) { document.title = title; setMeta("og:title", title, "property"); }
    if (desc) { setMeta("description", desc); setMeta("og:description", desc, "property"); }
    setMeta("og:type", "website", "property");
    if (image) {
      setMeta("og:image", image, "property");
      setMeta("twitter:image", image);
      setMeta("twitter:card", "summary_large_image");
    }
    if (path) {
      const urlFor = (l) => (l === "en" ? `${ORIGIN}${path}` : `${ORIGIN}/${l}${path}`);
      setLink("canonical", urlFor(lang));
      LANGS.forEach((l) => setLink("alternate", urlFor(l), l));
      setLink("alternate", urlFor("en"), "x-default");
    }
    setMeta("og:locale", OG_LOCALE[lang] || "en_US", "property");
    if (jsonLd) injectJsonLd(jsonLdId, jsonLd);
    return () => injectJsonLd(jsonLdId, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, title, desc, path, image, jsonKey, jsonLdId, loc.pathname]);
}

// Sets title, description, canonical, hreflang alternates and html lang for the current page.
export function useSeo(routeKey) {
  const { lang } = useI18n();
  const loc = useLocation();
  useEffect(() => {
    const meta = get((dict[lang] || {}).seo, routeKey) || get(dict.en.seo, routeKey) || {};
    if (meta.title) document.title = meta.title;
    if (meta.desc) {
      setMeta("description", meta.desc);
      setMeta("og:title", meta.title || document.title, "property");
      setMeta("og:description", meta.desc, "property");
    }
    document.documentElement.lang = lang;

    const base = routeKey === "/" ? "" : routeKey;
    const urlFor = (l) => (l === "en" ? `${ORIGIN}${base || "/"}` : `${ORIGIN}/${l}${base}`);
    setLink("canonical", urlFor(lang));
    LANGS.forEach((l) => setLink("alternate", urlFor(l), l));
    setLink("alternate", urlFor("en"), "x-default");
    setMeta("og:locale", OG_LOCALE[lang] || "en_US", "property");
  }, [lang, routeKey, loc.pathname]);
}

const CONTINENT_I18N = {
  en: { Europe: "Europe", Asia: "Asia", "North America": "North America", "South America": "South America", Africa: "Africa", Oceania: "Oceania" },
  pl: { Europe: "Europa", Asia: "Azja", "North America": "Ameryka Północna", "South America": "Ameryka Południowa", Africa: "Afryka", Oceania: "Oceania" },
  de: { Europe: "Europa", Asia: "Asien", "North America": "Nordamerika", "South America": "Südamerika", Africa: "Afrika", Oceania: "Ozeanien" },
  es: { Europe: "Europa", Asia: "Asia", "North America": "América del Norte", "South America": "América del Sur", Africa: "África", Oceania: "Oceanía" },
  pt: { Europe: "Europa", Asia: "Ásia", "North America": "América do Norte", "South America": "América do Sul", Africa: "África", Oceania: "Oceania" },
  fr: { Europe: "Europe", Asia: "Asie", "North America": "Amérique du Nord", "South America": "Amérique du Sud", Africa: "Afrique", Oceania: "Océanie" },
  it: { Europe: "Europa", Asia: "Asia", "North America": "America del Nord", "South America": "America del Sud", Africa: "Africa", Oceania: "Oceania" },
  tr: { Europe: "Avrupa", Asia: "Asya", "North America": "Kuzey Amerika", "South America": "Güney Amerika", Africa: "Afrika", Oceania: "Okyanusya" },
  ru: { Europe: "Европа", Asia: "Азия", "North America": "Северная Америка", "South America": "Южная Америка", Africa: "Африка", Oceania: "Океания" },
  ar: { Europe: "أوروبا", Asia: "آسيا", "North America": "أمريكا الشمالية", "South America": "أمريكا الجنوبية", Africa: "أفريقيا", Oceania: "أوقيانوسيا" },
  hi: { Europe: "यूरोप", Asia: "एशिया", "North America": "उत्तर अमेरिका", "South America": "दक्षिण अमेरिका", Africa: "अफ़्रीका", Oceania: "ओशिनिया" },
  zh: { Europe: "欧洲", Asia: "亚洲", "North America": "北美洲", "South America": "南美洲", Africa: "非洲", Oceania: "大洋洲" },
  ja: { Europe: "ヨーロッパ", Asia: "アジア", "North America": "北アメリカ", "South America": "南アメリカ", Africa: "アフリカ", Oceania: "オセアニア" },
  ko: { Europe: "유럽", Asia: "아시아", "North America": "북아메리카", "South America": "남아메리카", Africa: "아프리카", Oceania: "오세아니아" },
};
const CONTINENT_SLUG = { europe: "Europe", asia: "Asia", "north-america": "North America", "south-america": "South America", africa: "Africa", oceania: "Oceania" };
export function continentName(nameOrSlug, lang) {
  const en = CONTINENT_SLUG[nameOrSlug] || nameOrSlug;
  return (CONTINENT_I18N[lang] || CONTINENT_I18N.en)[en] || en;
}

const _dn = {};
export function countryName(code, lang, fallback) {
  if (!code) return fallback || "";
  try {
    _dn[lang] = _dn[lang] || new Intl.DisplayNames([lang], { type: "region" });
    const v = _dn[lang].of(code.toUpperCase());
    return v && v !== code.toUpperCase() ? v : (fallback || code);
  } catch { return fallback || code; }
}
